import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { getEnseignes, createEnseigne, createProjet, getEnseigneMagasins } from '../services/api';
import { ArrowLeft, Plus, Building2, Store, Check, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const STATUT_LABELS = { en_preparation: 'En préparation', en_cours: 'En cours', termine_facturation: 'Terminé – facturation', stand_by: 'Stand-by', cloture: 'Clôturé', projet_non_realise: 'Non réalisé' };
const eur0 = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);

/* Magasins déjà enregistrés pour l'enseigne (même lieu) → reprise des infos dans le nouveau projet */
const MagasinsExistants = ({ enseigneId, selectedKey, onPick, onClear }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    if (!enseigneId) return;
    getEnseigneMagasins(enseigneId).then((r) => setData(r.data)).catch(() => setData({ magasins: [] }));
  }, [enseigneId]);
  if (!enseigneId || !data || data.magasins.length === 0) return null;
  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4" data-testid="magasins-existants">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-indigo-900"><Store className="w-4 h-4" /> Magasin déjà enregistré ?</div>
        {selectedKey && <button type="button" onClick={onClear} className="text-xs text-indigo-700 hover:underline flex items-center gap-1" data-testid="magasin-clear-btn"><RotateCcw className="w-3 h-3" /> Nouveau lieu</button>}
      </div>
      <p className="text-xs text-indigo-800/80 mb-3">Reprenez les infos d'un magasin existant (adresse, surfaces, facturation). Le projet précédent reste tel quel ; le nouveau sera rattaché au même magasin.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {data.magasins.map((m) => {
          const active = selectedKey === m.key;
          return (
            <button key={m.key} type="button" onClick={() => onPick(m)} data-testid={`magasin-pick-${m.key.split('|')[1].replace(/\s+/g, '_')}`}
              className={`text-left rounded-lg border p-3 transition-colors ${active ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-slate-200 hover:border-indigo-400'}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{m.ville}</div>
                  {m.adresse && <div className={`text-xs truncate ${active ? 'text-indigo-100' : 'text-slate-500'}`}>{m.adresse}</div>}
                </div>
                {active && <Check className="w-4 h-4 shrink-0" />}
              </div>
              <div className={`mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] ${active ? 'text-indigo-100' : 'text-slate-500'}`}>
                <span>{m.nb_projets} projet{m.nb_projets > 1 ? 's' : ''}</span>
                <span>Dernier : {STATUT_LABELS[m.dernier_statut] || m.dernier_statut}</span>
                {Object.entries(m.ca_par_annee).sort().map(([a, v]) => <span key={a}>{a} : {eur0(v)} HT</span>)}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const NewProjetPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedEnseigne = searchParams.get('enseigne');
  
  const [enseignes, setEnseignes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showNewEnseigne, setShowNewEnseigne] = useState(false);
  
  const [form, setForm] = useState({
    enseigne_id: preselectedEnseigne || '',
    ville: '',
    adresse: '',
    statut: 'en_preparation',
    date_debut: '',
    date_fin: '',
    responsable: '',
    commentaires: '',
    surface_commerciale: '',
    surface_annexes: '',
    client_facturation: '',
    adresse_facturation: '',
    contact_facturation: '',
    telephone_facturation: '',
    email_facturation: '',
    magasin_source_projet_id: null
  });
  const [magasinKey, setMagasinKey] = useState(null);
  const pickMagasin = (m) => {
    setMagasinKey(m.key);
    setForm((f) => ({ ...f, ...Object.fromEntries(Object.entries(m.prefill).map(([k, v]) => [k, v ?? ''])), magasin_source_projet_id: m.dernier_projet_id }));
    toast.success(`Infos du magasin ${m.ville} reprises`);
  };
  const clearMagasin = () => {
    setMagasinKey(null);
    setForm((f) => ({ ...f, ville: '', adresse: '', surface_commerciale: '', surface_annexes: '', client_facturation: '', adresse_facturation: '', contact_facturation: '', telephone_facturation: '', email_facturation: '', responsable: '', magasin_source_projet_id: null }));
  };

  const [newEnseigne, setNewEnseigne] = useState({
    nom: '',
    adresse_siege: '',
    contact_principal: '',
    telephone: '',
    email: ''
  });

  useEffect(() => {
    loadEnseignes();
  }, []);

  const loadEnseignes = async () => {
    try {
      const res = await getEnseignes();
      setEnseignes(res.data);
      // Set preselected enseigne or first one
      if (preselectedEnseigne) {
        setForm(f => ({ ...f, enseigne_id: preselectedEnseigne }));
      } else if (res.data.length > 0) {
        setForm(f => (f.enseigne_id ? f : { ...f, enseigne_id: res.data[0].id }));
      }
    } catch (e) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEnseigne = async () => {
    if (!newEnseigne.nom) {
      toast.error('Le nom de l\'enseigne est requis');
      return;
    }
    try {
      const res = await createEnseigne(newEnseigne);
      toast.success('Enseigne créée');
      setEnseignes([...enseignes, res.data]);
      setForm({ ...form, enseigne_id: res.data.id });
      setShowNewEnseigne(false);
      setNewEnseigne({ nom: '', adresse_siege: '', contact_principal: '', telephone: '', email: '' });
    } catch (e) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.enseigne_id) {
      toast.error('Veuillez sélectionner une enseigne');
      return;
    }
    if (!form.ville) {
      toast.error('La ville est requise');
      return;
    }

    setCreating(true);
    try {
      const projetData = {
        ...form,
        surface_commerciale: parseFloat(form.surface_commerciale) || 0,
        surface_annexes: parseFloat(form.surface_annexes) || 0
      };
      const res = await createProjet(projetData);
      toast.success(magasinKey ? 'Projet créé et rattaché au magasin existant' : 'Projet créé');
      navigate(`/projets/${res.data.id}`);
    } catch (e) {
      toast.error('Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="new-projet-page" className="max-w-2xl mx-auto">
      <Link to="/projets" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900 mb-6">
        <ArrowLeft className="w-4 h-4 mr-1" />
        Retour aux projets
      </Link>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="px-6 py-4 border-b border-slate-200">
          <h1 className="text-xl font-bold text-slate-900">Nouveau projet</h1>
          <p className="text-sm text-slate-500">Créez un nouveau chantier</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Enseigne */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Enseigne (Client) *
            </label>
            {enseignes.length === 0 && !showNewEnseigne ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800 mb-2">Aucune enseigne. Créez-en une d'abord.</p>
                <button
                  type="button"
                  onClick={() => setShowNewEnseigne(true)}
                  className="text-sm font-medium text-amber-700 hover:text-amber-900"
                >
                  + Créer une enseigne
                </button>
              </div>
            ) : (
              <div className="flex gap-3">
                <select
                  value={form.enseigne_id}
                  onChange={(e) => { setForm({ ...form, enseigne_id: e.target.value }); setMagasinKey(null); }}
                  className="flex-1 h-10 px-4 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  data-testid="select-enseigne"
                >
                  {enseignes.map((e) => (
                    <option key={e.id} value={e.id}>{e.nom}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowNewEnseigne(!showNewEnseigne)}
                  className="h-10 px-4 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* New Enseigne Form */}
            {showNewEnseigne && (
              <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-sm font-medium text-slate-700 mb-3">Nouvelle enseigne</p>
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Nom de l'enseigne *"
                    value={newEnseigne.nom}
                    onChange={(e) => setNewEnseigne({ ...newEnseigne, nom: e.target.value })}
                    className="w-full h-10 px-4 rounded-lg border border-slate-300 text-sm"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      placeholder="Contact principal"
                      value={newEnseigne.contact_principal}
                      onChange={(e) => setNewEnseigne({ ...newEnseigne, contact_principal: e.target.value })}
                      className="h-10 px-4 rounded-lg border border-slate-300 text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Téléphone"
                      value={newEnseigne.telephone}
                      onChange={(e) => setNewEnseigne({ ...newEnseigne, telephone: e.target.value })}
                      className="h-10 px-4 rounded-lg border border-slate-300 text-sm"
                    />
                  </div>
                  <input
                    type="email"
                    placeholder="Email"
                    value={newEnseigne.email}
                    onChange={(e) => setNewEnseigne({ ...newEnseigne, email: e.target.value })}
                    className="w-full h-10 px-4 rounded-lg border border-slate-300 text-sm"
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowNewEnseigne(false)}
                      className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900"
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      onClick={handleCreateEnseigne}
                      className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800"
                    >
                      Créer l'enseigne
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Magasin existant (même enseigne, même lieu) */}
          <MagasinsExistants enseigneId={form.enseigne_id} selectedKey={magasinKey} onPick={pickMagasin} onClear={clearMagasin} />

          {/* Ville */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Ville / Site * {magasinKey && <span className="ml-2 text-xs font-normal text-indigo-700">(repris du magasin)</span>}
            </label>
            <input
              type="text"
              value={form.ville}
              onChange={(e) => setForm({ ...form, ville: e.target.value })}
              placeholder="Ex: Lyon Confluence"
              className="w-full h-10 px-4 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
              data-testid="input-ville"
              required
            />
          </div>

          {/* Adresse */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Adresse
            </label>
            <input
              type="text"
              value={form.adresse}
              onChange={(e) => setForm({ ...form, adresse: e.target.value })}
              placeholder="Adresse du chantier"
              className="w-full h-10 px-4 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
          </div>

          {/* Info devis types */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm font-medium text-blue-800">Aucun devis n'est créé automatiquement</p>
            <p className="text-xs text-blue-600 mt-1">
              Depuis la fiche projet, « Nouveau devis » vous proposera les modèles : Travaux centre-ville, Travaux centre commercial, Maintenance, Bureau d'étude.
            </p>
          </div>

          {/* Statut */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Statut initial
            </label>
            <select
              value={form.statut}
              onChange={(e) => setForm({ ...form, statut: e.target.value })}
              className="w-full h-10 px-4 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
            >
              <option value="en_preparation">🔵 En préparation</option>
              <option value="en_cours">🟢 En cours</option>
              <option value="stand_by">🟡 Stand-by</option>
            </select>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Date début estimée
              </label>
              <input
                type="date"
                value={form.date_debut}
                data-testid="projet-date-debut-input"
                onChange={(e) => {
                  const d = e.target.value;
                  setForm((f) => ({ ...f, date_debut: d, date_fin: (!f.date_fin || f.date_fin < d) ? d : f.date_fin }));
                }}
                className="w-full h-10 px-4 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Date fin estimée
              </label>
              <input
                type="date"
                data-testid="projet-date-fin-input"
                value={form.date_fin}
                min={form.date_debut || undefined}
                onChange={(e) => setForm({ ...form, date_fin: e.target.value })}
                className="w-full h-10 px-4 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
            </div>
          </div>

          {/* Surfaces */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Surface commerciale (m²)
              </label>
              <input
                type="number"
                value={form.surface_commerciale}
                onChange={(e) => setForm({ ...form, surface_commerciale: e.target.value })}
                placeholder="0"
                className="w-full h-10 px-4 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Surface annexes (m²)
              </label>
              <input
                type="number"
                value={form.surface_annexes}
                onChange={(e) => setForm({ ...form, surface_annexes: e.target.value })}
                placeholder="0"
                className="w-full h-10 px-4 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
            </div>
          </div>

          {/* Responsable */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Responsable / Pilote
            </label>
            <input
              type="text"
              value={form.responsable}
              onChange={(e) => setForm({ ...form, responsable: e.target.value })}
              placeholder="Nom du responsable"
              className="w-full h-10 px-4 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
          </div>

          {/* Commentaires */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Commentaires
            </label>
            <textarea
              value={form.commentaires}
              onChange={(e) => setForm({ ...form, commentaires: e.target.value })}
              rows={3}
              placeholder="Notes sur le projet..."
              className="w-full px-4 py-3 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 resize-none"
            />
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <Link
              to="/projets"
              className="px-6 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Annuler
            </Link>
            <button
              type="submit"
              disabled={creating}
              className="px-6 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
              data-testid="submit-projet"
            >
              {creating ? 'Création...' : 'Créer le projet'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewProjetPage;
